import React, { useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Card,
    Typography,
    IconButton,
    Stack,
    useTheme,
    MenuItem,
    Select,
    FormControl,
    Button,
    Alert,
    CircularProgress,
    Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import axios from 'axios';
import useAuth from '../../hooks/useAuth';
import { HOST_API } from '../../config';

const GenerateBills = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const [selectedWeek, setSelectedWeek] = useState('CURRENT');
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const { user } = useAuth();
    // Helper function to correctly detect if running in a Capacitor native environment
    // const isNativeApp = () => window.Capacitor?.isNativePlatform?.() === true;
    const isNativeApp = Capacitor.isNativePlatform();

    const mapWeekValue = (value) => {
        switch (value) {
            case 'current': return 'CURRENT';
            case 'previous': return 'PREVIOUS';
            case 'lastPrevious': return 'LAST_PREVIOUS';
            default: return 'CURRENT';
        }
    };
    // Helper function to get PDF data
    const fetchPdfData = async () => {
        const userId = user?._id;
        if (!userId) {
            setAlert({
                open: true,
                message: 'User ID not found. Please log in again.',
                severity: 'error'
            });
            return null;
        }

        const weekForReport = mapWeekValue(selectedWeek);

        // Make API call to get PDF buffer
        const response = await axios({
            url: `${HOST_API}/trade/client-settlement-report/${userId}`,
            method: 'GET',
            params: { week: weekForReport },
            responseType: 'arraybuffer',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
            },
        });

        // Generate filename with date
        const date = new Date();
        const fileName = `Settlement_Report_${date.toISOString().split('T')[0]}.pdf`;
        
        return {
            pdfData: response.data,
            fileName,
            blob: new Blob([response.data], { type: 'application/pdf' })
        };
    };

    // View Bill: Open the PDF from a temporary location
    const handleViewBill = async () => {
        try {
            setLoading(true);
            
            const result = await fetchPdfData();
            if (!result) {
                setLoading(false);
                return;
            }
            
            const { blob, fileName } = result;

            if (isNativeApp) {
                console.log('Running on native platform - view mode');
                try {
                    // Convert blob to base64
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = async function onLoadEnd() {
                        const base64Data = reader.result.split(',')[1];
                        try {
                            // Save to cache directory (temporary storage)
                            const cacheResult = await Filesystem.writeFile({
                                path: fileName,
                                data: base64Data,
                                directory: Directory.Cache,
                                recursive: true
                            });
                            
                            // Open the file with FileOpener
                            await FileOpener.open({
                                filePath: cacheResult.uri,
                                contentType: 'application/pdf'
                            });
                            
                            // setAlert({
                            //     open: true,
                            //     message: 'Opening bill for viewing',
                            //     severity: 'info'
                            // });
                            
                        } catch (error) {
                            console.error('Error in view handling:', error);
                            setAlert({
                                open: true,
                                message: `Unable to view bill: ${error.message}`,
                                severity: 'error'
                            });
                        }
                    };
                } catch (error) {
                    console.error('Error in file handling:', error);
                    setAlert({
                        open: true,
                        message: `Error: ${error.message}`,
                        severity: 'error'
                    });
                }
            } else {
                // For web browsers, open in new tab
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            }
        } catch (error) {
            console.error('Error viewing bill:', error);
            setAlert({
                open: true,
                message: `Error viewing bill: ${error.response?.data?.message || error.message}`,
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCloseAlert = () => {
        setAlert((prev) => ({ ...prev, open: false }));
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 800, mx: 'auto' }}>
            {/* Header */}
            <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ mb: 4 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        onClick={() => navigate(-1)}
                        sx={{
                            mr: 2,
                            width: 35,
                            height: 35,
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.1)'
                                : 'rgba(0,0,0,0.05)',
                            backdropFilter: 'blur(6px)',
                            '&:hover': {
                                bgcolor: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.2)'
                                    : 'rgba(0,0,0,0.1)',
                                transform: 'translateX(-3px)',
                            },
                            transition: 'all 0.2s',
                            border: `1px solid ${theme.palette.mode === 'dark'
                                ? 'rgba(255,255,255,0.12)'
                                : 'rgba(0,0,0,0.08)'
                                }`,
                        }}
                    >
                        <ArrowBackIcon
                            sx={{
                                fontSize: 20,
                                color: theme.palette.mode === 'dark'
                                    ? 'rgba(255,255,255,0.8)'
                                    : 'rgba(0,0,0,0.7)'
                            }}
                        />
                    </IconButton>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            background: theme.palette.mode === 'dark'
                                ? 'linear-gradient(45deg, #f3ec78 0%, #af4261 100%)'
                                : 'linear-gradient(45deg, #2c3e50 30%, #3498db 90%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        GENERATE BILLS
                    </Typography>
                </Box>
            </Stack>

            {/* Bill Generation Card */}
            <Card
                sx={{
                    p: 3,
                    borderRadius: 2,
                    background: theme.palette.mode === 'dark'
                        ? 'linear-gradient(135deg, rgba(45,45,60,0.95), rgba(45,45,60,0.9))'
                        : 'white',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 32px rgba(0,0,0,0.3)'
                        : '0 8px 32px rgba(145,158,171,0.16)',
                }}
            >
                <Stack spacing={3}>
                    <FormControl fullWidth>
                        <Select
                            value={selectedWeek}
                            onChange={(e) => setSelectedWeek(e.target.value)}
                            sx={{
                                height: 48,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.mode === 'dark'
                                        ? 'rgba(255,255,255,0.23)'
                                        : 'rgba(0,0,0,0.23)',
                                },
                            }}
                        >
                            <MenuItem value="current">Current Week</MenuItem>
                            <MenuItem value="previous">Previous Week</MenuItem>
                            <MenuItem value="lastPrevious">Last Previous Week</MenuItem>
                        </Select>
                    </FormControl>                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleViewBill}
                            disabled={loading}
                            sx={{
                                minWidth: 120,
                                height: 48,
                                textTransform: 'none',
                                fontSize: '1rem',
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'View Bill'}
                        </Button>
                    </Stack>
                </Stack>
            </Card>

            {/* Snackbar for alerts */}
            <Snackbar
                open={alert.open}
                autoHideDuration={6000}
                onClose={handleCloseAlert}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseAlert}
                    severity={alert.severity}
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GenerateBills;
